import { useEffect } from 'react';

/**
 * Raccourci clavier global. Exemples :
 *   useHotkey('mod+k', () => setPaletteOpen(true))
 *   useHotkey('Escape', onClose, { enabled: open })
 *
 * `mod` = Cmd sur macOS, Ctrl sinon. Ignore l'évènement quand le focus est
 * dans un `<input>`, `<textarea>` ou `[contenteditable]`, SAUF si la touche
 * est `Escape` ou si `allowInInput` est passé.
 */
export interface HotkeyOptions {
  enabled?: boolean;
  allowInInput?: boolean;
  preventDefault?: boolean;
}

export function useHotkey(
  combo: string,
  handler: (e: KeyboardEvent) => void,
  opts: HotkeyOptions = {},
): void {
  const enabled = opts.enabled ?? true;
  const preventDefault = opts.preventDefault ?? true;

  useEffect(() => {
    if (!enabled) return;
    const parsed = parseCombo(combo);

    function onKey(e: KeyboardEvent): void {
      if (!matches(parsed, e)) return;

      const target = e.target as HTMLElement | null;
      const isEditable =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable);

      if (isEditable && !opts.allowInInput && parsed.key !== 'escape') return;

      if (preventDefault) e.preventDefault();
      handler(e);
    }

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [combo, handler, enabled, opts.allowInInput, preventDefault]);
}

interface ParsedCombo {
  mod: boolean;
  shift: boolean;
  alt: boolean;
  key: string;
}

function parseCombo(combo: string): ParsedCombo {
  const parts = combo.toLowerCase().split('+');
  const result: ParsedCombo = { mod: false, shift: false, alt: false, key: '' };
  for (const p of parts) {
    if (p === 'mod' || p === 'cmd' || p === 'ctrl') result.mod = true;
    else if (p === 'shift') result.shift = true;
    else if (p === 'alt' || p === 'option') result.alt = true;
    else result.key = p;
  }
  return result;
}

function matches(c: ParsedCombo, e: KeyboardEvent): boolean {
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  const modPressed = isMac ? e.metaKey : e.ctrlKey;
  if (c.mod !== modPressed) return false;
  if (c.shift !== e.shiftKey) return false;
  if (c.alt !== e.altKey) return false;
  return e.key.toLowerCase() === c.key;
}

/** Retourne le symbole du modifier selon la plateforme. */
export function modKeySymbol(): string {
  if (typeof navigator === 'undefined') return 'Ctrl';
  return /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl';
}
